import { supabaseClient } from './supabaseClient';
import { UserProfile } from '@/types';
import { NarrativeGitService } from './narrativeGitService';

const narrativeGitService = new NarrativeGitService();
import { optimizedImageService } from './optimizedImageService';

export interface ShareableContent {
  id: string;
  type: 'progress' | 'achievement' | 'story_moment' | 'character' | 'combat_victory' | 'game_invite';
  title: string;
  description: string;
  imageUrl?: string;
  metadata: Record<string, any>;
  createdBy: string;
  createdAt: Date;
  expiresAt?: Date;
}

export interface ShareLink {
  id: string;
  shortCode: string;
  contentId: string;
  contentType: ShareableContent['type'];
  viewCount: number;
  maxViews?: number;
  expiresAt?: Date;
  password?: string;
  createdAt: Date;
}

export interface ShareStats {
  totalShares: number;
  viewsByType: Record<string, number>;
  topSharedContent: ShareableContent[];
  recentShares: ShareLink[];
}

class SharingService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = window.location.origin;
  }

  /**
   * Create a shareable link for game progress
   */
  async shareProgress(userId: string, gameSessionId: string, instanceId: string): Promise<ShareLink> {
    try {
      // Get current progress data
      const progressData = await narrativeGitService.getProgress(instanceId);
      if (!progressData) {
        throw new Error('No progress found to share');
      }

      // Get user data
      const { data: userData, error: userError } = await supabaseClient
        .from('users')
        .select('name, archetype, stats')
        .eq('id', userId)
        .single();

      if (userError) throw userError;

      // Create shareable content
      const content: Omit<ShareableContent, 'id' | 'createdAt'> = {
        type: 'progress',
        title: `${userData.name}'s Journey`,
        description: `Playing as ${userData.archetype} - Chapter ${progressData.segments.length}`,
        imageUrl: progressData.segments[progressData.segments.length - 1]?.imageUrl,
        metadata: {
          userId,
          gameSessionId,
          instanceId,
          archetype: userData.archetype,
          level: Math.floor((userData.stats?.experience || 0) / 100),
          chaptersCompleted: progressData.segments.length,
          choicesMade: progressData.choices.length,
          currentBranch: progressData.currentBranch
        },
        createdBy: userId
      };

      return this.createShareLink(content);
    } catch (error) {
      console.error('[SharingService] Error sharing progress:', error);
      throw error;
    }
  }

  /**
   * Create a shareable link for a story moment
   */
  async shareStoryMoment(
    userId: string, 
    storySegment: any,
    choice?: string
  ): Promise<ShareLink> {
    try {
      const content: Omit<ShareableContent, 'id' | 'createdAt'> = {
        type: 'story_moment',
        title: 'Epic Story Moment',
        description: this.truncateText(storySegment.narration, 200),
        imageUrl: storySegment.imageUrl,
        metadata: {
          userId,
          segmentId: storySegment.id,
          choice,
          timestamp: new Date().toISOString()
        },
        createdBy: userId
      };

      return this.createShareLink(content);
    } catch (error) {
      console.error('[SharingService] Error sharing story moment:', error);
      throw error;
    }
  }

  /**
   * Create a game invite link
   */
  async createGameInvite(
    hostUserId: string,
    gameMode: 'coop' | 'pvp' | 'party',
    roomId?: string,
    maxPlayers: number = 4
  ): Promise<ShareLink> {
    try {
      const { data: hostData, error } = await supabaseClient
        .from('users')
        .select('name, archetype')
        .eq('id', hostUserId)
        .single();

      if (error) throw error;

      const content: Omit<ShareableContent, 'id' | 'createdAt'> = {
        type: 'game_invite',
        title: `Join ${hostData.name}'s Game!`,
        description: `Playing ${gameMode} mode - ${maxPlayers - 1} spots available`,
        metadata: {
          hostUserId,
          hostName: hostData.name,
          gameMode,
          roomId: roomId || this.generateRoomId(),
          maxPlayers,
          joinedPlayers: [hostUserId]
        },
        createdBy: hostUserId,
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000) // 24 hours
      };

      return this.createShareLink(content, {
        expiresInHours: 24,
        maxViews: maxPlayers * 2 // Allow some buffer for rejoins
      });
    } catch (error) {
      console.error('[SharingService] Error creating game invite:', error);
      throw error;
    }
  }

  /**
   * Share character build/stats
   */
  async shareCharacter(user: UserProfile): Promise<ShareLink> {
    try {
      const level = Math.floor((user.stats?.experience || 0) / 100);
      
      const content: Omit<ShareableContent, 'id' | 'createdAt'> = {
        type: 'character',
        title: `${user.displayName || user.name} - Level ${level} ${user.archetype}`,
        description: `Karma: ${user.karma} | ${user.traits.join(', ')}`,
        imageUrl: user.avatarUrl,
        metadata: {
          userId: user.uid,
          archetype: user.archetype,
          level,
          stats: user.stats,
          traits: user.traits,
          karma: user.karma,
          achievements: user.achievements || []
        },
        createdBy: user.uid!
      };

      return this.createShareLink(content);
    } catch (error) {
      console.error('[SharingService] Error sharing character:', error);
      throw error;
    }
  }

  /**
   * Share combat victory
   */
  async shareCombatVictory(
    userId: string,
    combatSessionId: string,
    combatMode: 'pve' | 'pvp' | 'coop',
    rewards: any
  ): Promise<ShareLink> {
    try {
      const { data: userData, error } = await supabaseClient
        .from('users')
        .select('name, archetype')
        .eq('id', userId)
        .single();

      if (error) throw error;

      const content: Omit<ShareableContent, 'id' | 'createdAt'> = {
        type: 'combat_victory',
        title: `${userData.name} Achieved Victory!`,
        description: `Won a ${combatMode.toUpperCase()} battle and earned ${rewards.experience} XP`,
        metadata: {
          userId,
          combatSessionId,
          combatMode,
          rewards,
          timestamp: new Date().toISOString()
        },
        createdBy: userId
      };

      return this.createShareLink(content);
    } catch (error) {
      console.error('[SharingService] Error sharing combat victory:', error);
      throw error;
    }
  }

  /**
   * Create a shareable link from content
   */
  private async createShareLink(
    content: Omit<ShareableContent, 'id' | 'createdAt'>,
    options: {
      expiresInHours?: number;
      maxViews?: number;
      password?: string;
    } = {}
  ): Promise<ShareLink> {
    try {
      // Save content to database - map to actual database columns
      const { data: savedContent, error: contentError } = await supabaseClient
        .from('shareable_content')
        .insert({
          user_id: content.createdBy,
          share_type: content.type,
          content: {
            title: content.title,
            description: content.description,
            imageUrl: content.imageUrl
          },
          metadata: content.metadata,
          is_public: true,
          created_by: content.createdBy,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (contentError) throw contentError;

      // Generate short code
      const shortCode = await this.generateShortCode();

      // Create share link - map to actual database columns
      const { data: savedLink, error: linkError } = await supabaseClient
        .from('share_links')
        .insert({
          shareable_content_id: savedContent.id,
          short_code: shortCode,
          view_count: 0,
          max_views: options.maxViews || null,
          expires_at: options.expiresInHours 
            ? new Date(Date.now() + options.expiresInHours * 60 * 60 * 1000).toISOString()
            : null,
          password: options.password || null,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (linkError) throw linkError;

      // Map database columns back to interface
      return {
        id: savedLink.id,
        shortCode: savedLink.short_code,
        contentId: savedLink.shareable_content_id,
        contentType: content.type,
        viewCount: savedLink.view_count,
        createdAt: new Date(savedLink.created_at)
      };
    } catch (error) {
      console.error('[SharingService] Error creating share link:', error);
      throw error;
    }
  }

  /**
   * Get share link URL
   */
  getShareUrl(shareLink: ShareLink): string {
    return `${this.baseUrl}/share/${shareLink.shortCode}`;
  }

  /**
   * Generate social media share URLs
   */
  getSocialShareUrls(shareLink: ShareLink, content: ShareableContent) {
    const url = this.getShareUrl(shareLink);
    const text = `${content.title} - ${content.description}`;
    
    return {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      reddit: `https://reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(content.title)}`,
      discord: `${url}`, // Discord auto-embeds links
      email: `mailto:?subject=${encodeURIComponent(content.title)}&body=${encodeURIComponent(text + '\n\n' + url)}`
    };
  }

  /**
   * Resolve a share link and get content
   */
  async resolveShareLink(shortCode: string, password?: string): Promise<{
    content: ShareableContent;
    link: ShareLink;
  }> {
    try {
      // Get share link
      const { data: link, error: linkError } = await supabaseClient
        .from('share_links')
        .select('*')
        .eq('short_code', shortCode)
        .single();

      if (linkError || !link) {
        throw new Error('Invalid share link');
      }

      // Check if expired
      if (link.expiresAt && new Date(link.expiresAt) < new Date()) {
        throw new Error('Share link has expired');
      }

      // Check max views
      if (link.maxViews && link.viewCount >= link.maxViews) {
        throw new Error('Share link has reached maximum views');
      }

      // Check password
      if (link.password && link.password !== password) {
        throw new Error('Invalid password');
      }

      // Get content
      const { data: content, error: contentError } = await supabaseClient
        .from('shareable_content')
        .select('*')
        .eq('id', link.shareable_content_id)
        .single();

      if (contentError || !content) {
        throw new Error('Content not found');
      }

      // Increment view count
      await supabaseClient
        .from('share_links')
        .update({ view_count: (link.view_count || 0) + 1 })
        .eq('id', link.id);

      // Map database columns to interface
      const mappedContent: ShareableContent = {
        id: content.id,
        type: content.share_type as ShareableContent['type'],
        title: content.content?.title || '',
        description: content.content?.description || '',
        imageUrl: content.content?.imageUrl,
        metadata: content.metadata || {},
        createdBy: content.created_by || content.user_id,
        createdAt: new Date(content.created_at)
      };

      const mappedLink: ShareLink = {
        id: link.id,
        shortCode: link.short_code,
        contentId: link.shareable_content_id,
        contentType: content.share_type as ShareableContent['type'],
        viewCount: link.view_count || 0,
        createdAt: new Date(link.created_at)
      };

      return { content: mappedContent, link: mappedLink };
    } catch (error) {
      console.error('[SharingService] Error resolving share link:', error);
      throw error;
    }
  }

  /**
   * Get sharing statistics for a user
   */
  async getUserShareStats(userId: string): Promise<ShareStats> {
    try {
      // Get total shares
      const { count: totalShares } = await supabaseClient
        .from('shareable_content')
        .select('*', { count: 'exact' })
        .eq('created_by', userId);

      // Get views by type - need to join properly
      const { data: shareData } = await supabaseClient
        .from('share_links')
        .select(`
          view_count,
          shareable_content!inner (
            share_type,
            created_by
          )
        `)
        .eq('shareable_content.created_by', userId);

      const viewsByType = shareData?.reduce((acc, item) => {
        const shareType = item.shareable_content?.share_type || 'unknown';
        acc[shareType] = (acc[shareType] || 0) + (item.view_count || 0);
        return acc;
      }, {} as Record<string, number>) || {};

      // Get top shared content with view counts
      const { data: topContent } = await supabaseClient
        .from('shareable_content')
        .select(`
          *,
          share_links (
            view_count
          )
        `)
        .eq('created_by', userId)
        .limit(5);

      // Sort by total views manually since we can't order by aggregate
      const sortedTopContent = (topContent || [])
        .map(content => ({
          ...content,
          totalViews: content.share_links?.reduce((sum: number, link: any) => 
            sum + (link.view_count || 0), 0) || 0
        }))
        .sort((a, b) => b.totalViews - a.totalViews)
        .slice(0, 5);

      // Get recent shares with content info
      const { data: recentLinks } = await supabaseClient
        .from('share_links')
        .select(`
          *,
          shareable_content!inner (
            share_type,
            created_by
          )
        `)
        .eq('shareable_content.created_by', userId)
        .order('created_at', { ascending: false })
        .limit(10);

      // Map the recent links to our interface
      const mappedRecentLinks = (recentLinks || []).map(link => ({
        id: link.id,
        shortCode: link.short_code,
        contentId: link.shareable_content_id,
        contentType: link.shareable_content?.share_type as ShareableContent['type'],
        viewCount: link.view_count || 0,
        createdAt: new Date(link.created_at)
      }));

      return {
        totalShares: totalShares || 0,
        viewsByType,
        topSharedContent: sortedTopContent || [],
        recentShares: mappedRecentLinks
      };
    } catch (error) {
      console.error('[SharingService] Error getting share stats:', error);
      throw error;
    }
  }

  /**
   * Helper to generate short codes
   */
  private async generateShortCode(): Promise<string> {
    try {
      // Use database function to ensure uniqueness
      const { data, error } = await supabaseClient
        .rpc('generate_unique_short_code');
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.warn('[SharingService] Database function not available, using local generation');
      // Fallback to local generation
      const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789'; // Removed ambiguous chars
      let code = '';
      for (let i = 0; i < 8; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return code;
    }
  }

  /**
   * Helper to generate room IDs
   */
  private generateRoomId(): string {
    return `room-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Helper to truncate text
   */
  private truncateText(text: string, maxLength: number): string {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  }
}

export const sharingService = new SharingService();